import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Avatar from '../ui/Avatar';

/**
 * Sortable Kanban card. Shows candidate, interview title, scheduled date,
 * status badge, and (when a session is linked) the risk score and scorecard
 * recommendation.
 *
 * Click handler bubbles up via `onSelect(card)` so the parent can open the
 * detail drawer. The card stays draggable - the click only fires when the
 * pointer didn't move (DnD activation distance handles the disambiguation).
 */
export default function PipelineCard({ card, isDragging, onSelect }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: card.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const scheduled = card.scheduledAt
    ? new Date(card.scheduledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  const statusBadge =
    card.status === 'ACTIVE'    ? 'chip-teal' :
    card.status === 'COMPLETED' ? 'chip-navy' :
    card.status === 'CANCELLED' ? 'chip-rose' :
                                  'chip-teal';

  const risk = typeof card.riskScore === 'number' ? Math.round(card.riskScore) : null;
  const riskBadge =
    risk == null   ? null :
    risk >= 70     ? 'chip-rose' :
    risk >= 40     ? 'chip-amber' :
                     'chip-teal';

  const rec = card.scorecardRecommendation;
  const recBadge =
    !rec                                         ? null :
    /strong\s*hire|hire/i.test(rec)              ? 'chip-teal-solid' :
    /no\s*hire|reject/i.test(rec)                ? 'chip-rose'       :
                                                   'chip-navy';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onSelect?.(card)}
      className="bg-surface rounded-xl border border-line p-3 shadow-xs
                 cursor-grab active:cursor-grabbing select-none
                 hover:border-teal-400/80 hover:shadow-sm transition-all"
    >
      <div className="flex items-center gap-2 mb-1">
        <Avatar
          name={card.candidateName || 'Unknown'}
          email={card.candidateEmail || ''}
          size="sm"
        />
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">
            {card.candidateName || '-'}
          </p>
          <p className="text-xs text-slate-400 truncate">
            {card.candidateEmail || ''}
          </p>
        </div>
      </div>

      {card.title && (
        <p className="text-xs text-slate-600 mt-1 truncate">{card.title}</p>
      )}
      {scheduled && (
        <p className="text-xs text-slate-400 mt-1">{scheduled}</p>
      )}

      <div className="flex items-center flex-wrap gap-1.5 mt-2">
        <span className={`text-[10px] font-semibold uppercase tracking-wide
                          px-1.5 py-0.5 rounded ${statusBadge}`}>
          {card.status || 'PENDING'}
        </span>
        {riskBadge && (
          <span className={`text-[10px] font-semibold tracking-wide
                            px-1.5 py-0.5 rounded ${riskBadge}`}>
            Risk {risk}
          </span>
        )}
        {recBadge && (
          <span className={`text-[10px] font-semibold uppercase tracking-wide
                            px-1.5 py-0.5 rounded ${recBadge}`}
                title={rec}>
            {rec.length > 12 ? rec.slice(0, 12) + '…' : rec}
          </span>
        )}
      </div>
    </div>
  );
}
